import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { CHAIN_IDS } from '@/constants/chain';

import { normalizeTokenAddress } from '@/utils/normalizeAddress';

import { tokensSelector, useTokenStore } from '../store/token';
import {
  TokenResult,
  TokensByAddressesQuery,
  TokensResponse,
} from '../utils/0x/token-registry.types';
import { getFullURL, handleResponse } from '../utils/fetcher';
import { routes } from '../utils/routes';

const TOKEN_ADDRESSES_LIMIT = 50;

/**
 * Fetch token results from the internal API in chunked batches.
 * Groups addresses per chain and respects TOKEN_ADDRESSES_LIMIT per request.
 */
export const fetchTokenResultsByChain = async (
  addressesByChain: Map<number, string[]>,
): Promise<TokenResult[]> => {
  const fetches: Promise<TokenResult[]>[] = [];

  for (const [chainId, addresses] of addressesByChain) {
    for (let i = 0; i < addresses.length; i += TOKEN_ADDRESSES_LIMIT) {
      const chunk = addresses.slice(i, i + TOKEN_ADDRESSES_LIMIT);
      const params = new URLSearchParams({
        addresses: chunk.join(','),
        chainId: String(chainId),
      });
      const url = getFullURL(`${routes.api.SEARCH_TOKENS}?${params}`);
      fetches.push(
        fetch(url)
          .then(handleResponse<TokensResponse>)
          .then((res) => ('data' in res ? res.data : [])),
      );
    }
  }

  const results = await Promise.all(fetches);
  return results.flat();
};

interface TokenMap {
  [chainId: number]: {
    [address: string]: TokenResult;
  };
}

export const tokenResultsQueryKey = (params: TokensByAddressesQuery | undefined) =>
  ['token-results', params] as const;

export const tokenResultsMultiChainQueryKey = (addresses: string[], chainIds: number[]) =>
  ['token-results-multi-chain', addresses, chainIds] as const;

/**
 * Get token results for many tokens by contract address and chain ID.
 */
export default function useTokenResults(
  tokenAddresses: string[] | undefined,
  chainId: number | undefined,
): {
  isLoading: boolean;
  tokenMap: TokenMap | undefined;
} {
  const { tokens: allTokens, addTokens } = useTokenStore(tokensSelector);

  const tokens = useMemo(
    () =>
      tokenAddresses && chainId
        ? tokenAddresses.reduce(
            (obj, tokenAddress) => {
              if (!obj[chainId]) obj[chainId] = {};
              const key = normalizeTokenAddress(tokenAddress, chainId);
              obj[chainId][key] = allTokens[chainId]?.[key]?.token;
              return obj;
            },
            {} as { [chainId: number]: { [contractAddress: string]: TokenResult } },
          )
        : undefined,
    [allTokens, tokenAddresses, chainId],
  );

  const params: TokensByAddressesQuery | undefined = useMemo(
    () =>
      tokenAddresses?.length && chainId
        ? { addresses: tokenAddresses, chainId: [chainId] }
        : undefined,
    [chainId, tokenAddresses],
  );

  const tokenResultsFetcher = useCallback(async (): Promise<TokensResponse> => {
    if (!tokenAddresses?.length || !chainId) return { data: [], resultCount: 0 };

    const addressesByChain = new Map([[chainId, [...tokenAddresses]]]);
    const results = await fetchTokenResultsByChain(addressesByChain);
    return { data: results, resultCount: results.length };
  }, [chainId, tokenAddresses]);

  const { isLoading } = useQuery<TokensResponse, Error>({
    queryKey: tokenResultsQueryKey(params),
    queryFn: async () => {
      const tokenResults = await tokenResultsFetcher();
      if ('data' in tokenResults && tokenResults.data) {
        addTokens(tokenResults.data);
      }
      return tokenResults;
    },
    enabled: !!params,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return { isLoading, tokenMap: tokens };
}

/**
 * Get token results for many tokens by contract address across multiple chain IDs.
 */
export function useTokenResultsMultiChain(
  tokenAddresses: string[] | undefined,
  chainIds: number[] | undefined,
): {
  isLoading: boolean;
  tokenMap: TokenMap | undefined;
} {
  const { tokens: allTokens, addTokens } = useTokenStore(tokensSelector);

  const { tokenMap, addressesToRequest, chainIdsToRequest } = useMemo(() => {
    const addressesToRequest = new Set<string>();
    const chainIdsToRequest = new Set<number>();

    const tokenMap =
      tokenAddresses && chainIds
        ? chainIds.reduce((chainObj, chainId) => {
            chainObj[chainId] = tokenAddresses.reduce(
              (tokenObj, tokenAddress) => {
                const key = normalizeTokenAddress(tokenAddress, chainId);
                tokenObj[key] = allTokens[chainId]?.[key]?.token;

                if (!tokenObj[key]) {
                  const address = chainId === CHAIN_IDS.SOLANA ? tokenAddress : key;
                  addressesToRequest.add(address);
                  chainIdsToRequest.add(chainId);
                }

                return tokenObj;
              },
              {} as { [contractAddress: string]: TokenResult },
            );
            return chainObj;
          }, {} as TokenMap)
        : undefined;

    return {
      tokenMap,
      addressesToRequest: Array.from(addressesToRequest),
      chainIdsToRequest: Array.from(chainIdsToRequest),
    };
  }, [allTokens, tokenAddresses, chainIds]);

  const enabled = addressesToRequest.length > 0 && chainIdsToRequest.length > 0;

  const multiChainFetcher = useCallback(async (): Promise<TokensResponse> => {
    const fetches: Promise<TokenResult[]>[] = [];
    const chainIdParam = chainIdsToRequest.join(',');

    for (let i = 0; i < addressesToRequest.length; i += TOKEN_ADDRESSES_LIMIT) {
      const chunk = addressesToRequest.slice(i, i + TOKEN_ADDRESSES_LIMIT);
      const params = new URLSearchParams({
        addresses: chunk.join(','),
        chainId: chainIdParam,
      });
      const url = getFullURL(`${routes.api.SEARCH_TOKENS}?${params}`);
      fetches.push(
        fetch(url)
          .then(handleResponse<TokensResponse>)
          .then((res) => ('data' in res ? res.data : [])),
      );
    }

    const results = await Promise.all(fetches);
    return { data: results.flat(), resultCount: results.flat().length } as TokensResponse;
  }, [addressesToRequest, chainIdsToRequest]);

  const { isLoading } = useQuery<TokensResponse, Error>({
    queryKey: tokenResultsMultiChainQueryKey(addressesToRequest, chainIdsToRequest),
    queryFn: async () => {
      const tokenResults = await multiChainFetcher();
      if ('data' in tokenResults && tokenResults.data) {
        addTokens(tokenResults.data);
      }
      return tokenResults;
    },
    enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return { isLoading, tokenMap };
}
